import React, { useEffect, useState } from "react";
import Tooltip from "@8x8/oxygen-tooltip";
import styled from "styled-components";
import Card, { Header, Separator, Statistics } from "@8x8/oxygen-card";
import { IconButton } from "@8x8/oxygen-icon-button";
import { PencilInBoxIcon } from "@8x8/oxygen-icon";
import { CopyIcon, DownloadIcon, TrashIcon } from "@8x8/oxygen-icon/icons";
import { COLORS } from "@/react/theme/constants";
import { RuleSet } from "@/react/features/support-tools/dialplans/ui-components/RuleSet";
import Modal, {
  MODAL_SIZE,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@8x8/oxygen-modal";
import DialplanForm from "@/react/features/support-tools/dialplans/ui-components/DialplanForm";
import { Button } from "@/react/styled";
import { CountryFlags } from "@/react/features/support-tools/dialplans/CountryFlags";
import { useConvertDialplanToXmlFileMutation } from "@/react/features/support-tools/dialplans/xmlBasedApi";
import {
  useCreateDialplanMutation,
  useDeleteDialplanMutation,
  useUpdateDialplanMutation,
} from "@/react/services/common/customerDialplansApi";
import { OverlaySpinner } from "@/react/components/OverlaySpinner";
import useDialplansApi from "@/react/features/support-tools/dialplans/useDialplansApi";
import { TABS } from "../constants";
import {
  useCreateGlobalDialplanMutation,
  useUpdateGlobalDialplanMutation,
} from "@/react/features/support-tools/dialplans/globalDialplansApi";

const StyledCard = styled(Card)`
  width: 100%;
  height: 100%;
`;

const StyledHeader = styled(Header)`
  display: flex;
  margin: 16px;
  justify-content: space-between;
  align-items: center;
`;

const ActionsWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin: 16px;
`;

const StyledTrashIcon = styled(TrashIcon)`
  color: ${COLORS.danger};
  fill: currentColor;
`;

const RuleSetWrapper = styled.div`
  margin: 16px;
`;

const MODAL_OPTIONS = Object.freeze({
  EDIT: "EDIT",
  CLONE: "CLONE",
});

const displayNumber = (value) =>
  typeof value === "string" && value.trim() !== "" && /^\d+$/.test(value.trim())
    ? value
    : "N/A";

export const DialplanCard = React.memo(
  ({ dialplan, globalExpandState, resetTrigger, tab, dialplans }) => {
    const [downloadXmlFile] = useConvertDialplanToXmlFileMutation();
    const [isExpanded, setIsExpanded] = useState(false);
    const [isModalOpenFor, setIsModalOpenFor] = useState(null);
    const [portalTarget, setPortalTarget] = useState(null);
    const [isActionTriggered, setIsActionTriggered] = useState(false);
    const [
      createCustomerDialplanApi,
      { isLoading: isCreatingCustomerDialplan },
    ] = useCreateDialplanMutation();
    const [
      updateCustomerDialplanApi,
      { isLoading: isUpdatingCustomerDialplan },
    ] = useUpdateDialplanMutation();
    const [
      deleteCustomerDialplanApi,
      { isLoading: isDeletingCustomerDialplan },
    ] = useDeleteDialplanMutation();
    const [createGlobalDialplanApi, { isLoading: isCreatingGlobalDialplan }] =
      useCreateGlobalDialplanMutation();
    const [updateGlobalDialplanApi, { isLoading: isUpdatingGlobalDialplan }] =
      useUpdateGlobalDialplanMutation();

    useEffect(() => {
      if (globalExpandState !== null) {
        setIsExpanded(globalExpandState);
      }
    }, [globalExpandState, resetTrigger]);

    const statistics = [
      {
        statistics: displayNumber(dialplan.countryCode),
        label: "Country Code",
      },
      {
        statistics: displayNumber(dialplan.internationalPrefix),
        label: "Intl. Prefix",
      },
      {
        statistics: displayNumber(dialplan.nationalPrefix),
        label: "National Prefix",
      },
    ];

    const { handleUpdate, handleCreate, handleDelete, handleDownloadXML } =
      useDialplansApi({
        createDialplanApi:
          tab === TABS.GLOBAL
            ? createGlobalDialplanApi
            : createCustomerDialplanApi,
        updateDialplanApi:
          tab === TABS.GLOBAL
            ? updateGlobalDialplanApi
            : updateCustomerDialplanApi,
        // Global Dialplans can't be deleted via TAP Tools
        deleteDialplanApi: deleteCustomerDialplanApi,
        downloadXmlFile,
        dialplan,
        setIsModalOpenFor,
        setIsActionTriggered,
      });

    const isLoading =
      isUpdatingCustomerDialplan ||
      isUpdatingGlobalDialplan ||
      isCreatingCustomerDialplan ||
      isCreatingGlobalDialplan ||
      isDeletingCustomerDialplan;

    return (
      <>
        {isModalOpenFor && (
          <Modal
            shouldCloseOnOverlayClick
            shouldCloseOnEsc
            onClose={() => setIsModalOpenFor(null)}
            portalRef={(e) => setPortalTarget(e)}
            size={MODAL_SIZE.BIG}
          >
            <ModalHeader
              title={`${
                isModalOpenFor === MODAL_OPTIONS.EDIT ? "Edit" : "Create"
              } Dial Plan`}
              onClose={() => setIsModalOpenFor(null)}
            ></ModalHeader>
            <ModalContent>
              <DialplanForm
                initialDialplan={{
                  ...dialplan,
                  id: isModalOpenFor === MODAL_OPTIONS.EDIT && dialplan.id,
                }}
                menuPortalTarget={portalTarget}
                isActionTriggered={isActionTriggered}
                action={
                  isModalOpenFor === MODAL_OPTIONS.EDIT
                    ? handleUpdate
                    : handleCreate
                }
                dialplans={dialplans}
              />
            </ModalContent>
            <ModalFooter>
              {isModalOpenFor === MODAL_OPTIONS.EDIT ? (
                <Button onClick={() => setIsActionTriggered(true)}>
                  Update
                </Button>
              ) : (
                <Button onClick={() => setIsActionTriggered(true)}>
                  Create
                </Button>
              )}
              <Button
                variant={"secondary"}
                onClick={() => setIsModalOpenFor(null)}
              >
                Close
              </Button>
            </ModalFooter>
          </Modal>
        )}

        <StyledCard>
          <StyledHeader>
            {dialplan.name}
            <CountryFlags countryCode={dialplan.countryCode} />
          </StyledHeader>
          <Separator />
          <Statistics data={statistics} />
          {isExpanded &&
            dialplan.ruleSet.map((ruleSet) => (
              <RuleSetWrapper>
                <RuleSet {...ruleSet} />
              </RuleSetWrapper>
            ))}
          <ActionsWrapper>
            <Tooltip title={"Edit"}>
              <IconButton
                data-test-id="dialplan__card--edit-button"
                onClick={() => setIsModalOpenFor(MODAL_OPTIONS.EDIT)}
              >
                <PencilInBoxIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={"Clone"}>
              <IconButton
                onClick={() => setIsModalOpenFor(MODAL_OPTIONS.CLONE)}
              >
                <CopyIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={"Download XML"}>
              <IconButton onClick={handleDownloadXML}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            {tab === TABS.CUSTOMER && (
              <Tooltip title={"Delete"}>
                <IconButton onClick={handleDelete}>
                  <StyledTrashIcon />
                </IconButton>
              </Tooltip>
            )}
          </ActionsWrapper>
        </StyledCard>
        {isLoading && <OverlaySpinner />}
      </>
    );
  }
);
